import React, { Fragment, useCallback, useMemo, useState } from 'react';
import { Input } from '@chaser/components';
import MappingTableRow, { MappingTableRowContext } from '../../TableRow';
import MappingField from '../../Field';
import { getConsumedStatuses, MappingTypeStatusStatuses } from '../Status';
import { useReadOnly } from '../../readOnlyContext';

const onlyUnique = (value, index, self) => self.indexOf(value) === index;

export const getConsumedColumns = value => {
  const statuses = getConsumedStatuses(value?.statuses);

  return Object.fromEntries(
    (value?.contacts || []).flatMap(c => [
      [c.firstNameColumnId, true],
      [c.emailAddressColumnId, true],
      [c.statusColumnId, statuses],
      [c.lastNameColumnId, true],
    ]),
  );
};

const valuesMapper = {
  FirstName: {
    read: obj => ({ columnId: obj.firstNameColumnId }),
    write: obj => ({ firstNameColumnId: obj.FirstName?.columnId }),
  },
  LastName: {
    read: obj => ({
      columnId: obj.lastNameColumnId,
      splitFromFirstName: obj.splitFromFirstName,
    }),
    write: obj => ({
      lastNameColumnId: obj.LastName?.columnId,
      splitFromFirstName: obj.LastName?.splitFromFirstName || false,
    }),
  },
  EmailAddress: {
    read: obj => ({ columnId: obj.emailAddressColumnId }),
    write: obj => ({ emailAddressColumnId: obj.EmailAddress?.columnId || '' }),
  },
  Status: {
    read: obj => ({ columnId: obj.statusColumnId }),
    write: obj => ({ statusColumnId: obj.Status?.columnId || '' }),
  },
};

const getNewContact = () => ({
  splitFromFirstName: false,
  firstNameColumnId: null,
  lastNameColumnId: null,
  emailAddressColumnId: '',
  statusColumnId: '',
});

const MappingTypeAdditionalContacts = ({ field, value, onChange, availableColumns }) => {
  const [originalContacts] = useState(() => value?.contacts || []);

  const setNumberOfContacts = useCallback(
    e => {
      const n = Number(e.target.value);
      const now = Date.now();

      if (n === 0) {
        onChange(null);
        return;
      }

      onChange({
        statuses: [],
        ...value,
        numberOfContacts: n,
        contacts: new Array(n)
          .fill(null)
          .map(
            (_, index) =>
              value?.contacts?.[index] ||
              originalContacts[index] || { _id: String(now + index), ...getNewContact() },
          ),
      });
    },
    [onChange, value, originalContacts],
  );

  const setValueForContact = useCallback(
    (contactId, fieldId, cb) => {
      onChange({
        ...value,
        contacts: value.contacts.map(contact =>
          contact._id === contactId
            ? {
                ...contact,
                ...valuesMapper[fieldId].write(cb({})),
              }
            : contact,
        ),
      });
    },
    [onChange, value],
  );

  const availableStatuses = useMemo(
    () =>
      (value?.contacts || [])
        .map(c => c.statusColumnId)
        .flatMap(c => availableColumns[c]?.availableValues || [])
        .filter(onlyUnique)
        .map(v => ({ value: v, label: v })),
    [availableColumns, value],
  );

  const onStatusesChange = useCallback(
    statuses => {
      onChange({ ...value, statuses });
    },
    [onChange, value],
  );

  const fieldStatuses = useMemo(
    () =>
      field.fields
        .find(f => f.id === 'Status')
        .statuses.map(status => ({
          ...status,
          name: `Additional Contact Status: ${status.name}`,
        })),
    [field],
  );

  const readOnly = useReadOnly();

  return (
    <>
      <MappingTableRow field={field}>
        <Input
          type="number"
          width="full"
          value={value?.numberOfContacts || ''}
          min={0}
          onChange={setNumberOfContacts}
          disabled={readOnly}
        />
      </MappingTableRow>

      {value?.contacts?.map((contact, index) => (
        <Fragment key={contact._id}>
          {field.fields.map(field => (
            <MappingField
              key={field.id}
              field={{
                ...field,
                name: `Additional Contact ${index + 1}: ${field.name}`,
              }}
              value={valuesMapper[field.id]?.read(contact)}
              availableColumns={availableColumns}
              setUsedColumns={() => {}}
              setValue={cb => setValueForContact(contact._id, field.id, cb)}
            />
          ))}
        </Fragment>
      ))}

      <MappingTableRowContext.Provider value={{ isNested: true }}>
        {value?.numberOfContacts > 0 && (
          <MappingTypeStatusStatuses
            valueStatuses={value?.statuses}
            fieldStatuses={fieldStatuses}
            options={availableStatuses}
            onChange={onStatusesChange}
          />
        )}
      </MappingTableRowContext.Provider>
    </>
  );
};

export const Component = MappingTypeAdditionalContacts;
